import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema } from '../common/index.js';

/** Messagerie, file d'envois, relances, modèles, aperçu de lien (architecture §1.4, §2.6). */

export const CanalSchema = z.enum(['whatsapp', 'email', 'sms', 'postal']).meta({ id: 'Canal' });

export const ChannelAccountSchema = z
  .object({ id: IdSchema, label: z.string().min(1), kind: CanalSchema, requiresValidation: z.boolean() })
  .meta({ id: 'ChannelAccount', description: 'Compte émetteur (« WhatsApp d’Isabelle », e-mail du cabinet…).' });

export const ThreadSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema.nullable(),
    personId: IdSchema.nullable(),
    channelAccount: ChannelAccountSchema,
    externalContact: z.string().min(1),
    status: z.enum(['rattache', 'non_rattache']),
    lastMessageAt: InstantSchema,
    lastMessagePreview: z.string(),
    unreadCount: z.number().int().min(0),
  })
  .meta({ id: 'Thread', description: 'Fil de la boîte unifiée.' });

export const AttachmentSchema = z
  .object({
    documentVersionId: IdSchema,
    mime: z.string().min(3),
    thumbnailUrl: z.string().nullable(),
    pages: z.number().int().min(1).nullable(),
    durationS: z.number().min(0).nullable(),
  })
  .meta({ id: 'Attachment' });

export const MessageSchema = z
  .object({
    id: IdSchema,
    threadId: IdSchema,
    direction: z.enum(['entrant', 'sortant']),
    body: z.string(),
    sentAt: InstantSchema,
    status: z.enum(['envoye', 'delivre', 'lu', 'echec', 'recu']),
    senderDisplayName: z.string().nullable(),
    attachments: z.array(AttachmentSchema),
    transcript: z.object({ text: z.string(), status: z.enum(['a_verifier', 'relu']) }).nullable(),
  })
  .meta({ id: 'Message' });

export const ThreadDetailSchema = ThreadSchema.extend({ messages: z.array(MessageSchema) }).meta({ id: 'ThreadDetail' });

export const ThreadAttachSchema = z.object({ dossierId: IdSchema, personId: IdSchema.nullable() }).meta({ id: 'ThreadAttach' });

export const OutboundStateSchema = z
  .enum(['draft', 'pending_validation', 'scheduled', 'sending', 'sent', 'delivered', 'read', 'rejected', 'cancelled', 'failed'])
  .meta({ id: 'OutboundState', description: 'Machine à états de la file d’envois (outbox).' });

export const OutboundMessageSchema = z
  .object({
    id: IdSchema,
    channelAccountId: IdSchema,
    personId: IdSchema,
    dossierId: IdSchema,
    templateCode: z.string().nullable(),
    renderedBody: z.string().min(1),
    scheduledAt: InstantSchema.nullable(),
    state: OutboundStateSchema,
    validationRequiredByRole: z.enum(['dirigeante']).nullable(),
    attempts: z.number().int().min(0),
  })
  .meta({ id: 'OutboundMessage' });

export const OutboundCreateSchema = z
  .object({
    channelAccountId: IdSchema,
    personId: IdSchema,
    dossierId: IdSchema,
    templateCode: z.string().nullable(),
    body: z.string().min(1),
    scheduledAt: InstantSchema.nullable(),
    attachmentIds: z.array(IdSchema).default([]),
  })
  .meta({ id: 'OutboundCreate' });

export const OutboundValidationSchema = z
  .object({ decision: z.enum(['valider', 'rejeter']), reason: z.string().optional() })
  .meta({ id: 'OutboundValidation' });

export const LandingSchema = z
  .enum(['accueil', 'piece', 'paiement', 'confidentialite', 'progression'])
  .meta({ id: 'Landing', description: 'Page d’arrivée d’un lien personnel.' });

export const LinkPreviewSchema = z
  .object({ siteName: z.string(), title: z.string(), description: z.string(), image: z.string().nullable(), landing: LandingSchema })
  .meta({ id: 'LinkPreview', description: 'Aperçu Open Graph générique : jamais de nom ni de donnée personnelle.' });

export const FollowupPlanSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    personId: IdSchema.nullable(),
    organisation: z.string().nullable(),
    cadenceName: z.string().min(1),
    state: z.enum(['actif', 'pause', 'arrete', 'termine']),
    pausedReason: z.string().nullable(),
    pausedUntil: LocalDateSchema.nullable(),
    nextActionAt: InstantSchema.nullable(),
    nextAction: z
      .object({
        kind: z.enum(['message', 'appel', 'decision', 'recurrent_mensuel']),
        channel: CanalSchema.nullable(),
        templateCode: z.string().nullable(),
      })
      .nullable(),
  })
  .meta({ id: 'FollowupPlan', description: 'Plan de relance d’une personne ou d’un organisme.' });

export const FollowupPauseSchema = z
  .object({ reason: z.string().min(3), until: LocalDateSchema.nullable() })
  .meta({ id: 'FollowupPause', description: 'Pause « on laisse respirer », avec date de reprise proposée.' });

export const CadenceSimulationRequestSchema = z
  .object({ dossierId: IdSchema, personId: IdSchema, from: InstantSchema })
  .meta({ id: 'CadenceSimulationRequest' });

export const CadenceSimulationSchema = z
  .object({
    actions: z.array(
      z.object({
        at: InstantSchema,
        kind: z.enum(['message', 'appel', 'decision', 'recurrent_mensuel']),
        channel: CanalSchema.nullable(),
        templateCode: z.string().nullable(),
        blockedBy: z.string().nullable(),
      }),
    ),
  })
  .meta({ id: 'CadenceSimulation', description: 'Simulation sans effet de la cadence de relance.' });

export const TemplateSchema = z
  .object({
    code: z.string().regex(/^[A-Z]\d$/),
    channel: z.enum(['whatsapp', 'email', 'postal']),
    recipient: z.enum(['famille', 'notaire', 'organisme']),
    versionNo: z.number().int().min(1),
    status: z.enum(['brouillon', 'valide_isabelle']),
    body: z.string().min(1),
    variables: z.array(z.string()),
    lints: z.array(z.object({ code: z.string(), message: z.string() })),
  })
  .meta({ id: 'Template', description: 'Version d’un modèle de message.' });

export const TemplateVersionCreateSchema = z.object({ body: z.string().min(1) }).meta({ id: 'TemplateVersionCreate' });
